import axios from 'axios';
import { getStoredToken } from '../../login/api/loginApi';
import { attachAuthInterceptors } from '../../_shared/auth/authInterceptor';
import type {
  MyProfileResponse,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  AvatarUploadResponse,
} from '../types/myProfile';

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE ?? '/api/eraf',
  headers: { 'Content-Type': 'application/json' },
});
attachAuthInterceptors(apiClient);

// Request interceptor — attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — unwrap ApiResponse<T>
apiClient.interceptors.response.use(
  (response) => {
    const d = response.data as Record<string, unknown> | undefined;
    if (d && typeof d === 'object' && 'success' in d) {
      if (d.success === true) {
        return { ...response, data: d['data'] };
      }
      const err = d['error'] as Record<string, unknown> | undefined;
      return Promise.reject({
        code: err?.['code'] ?? `HTTP-${response.status}`,
        message: err?.['message'] ?? 'Unknown error',
        traceId: (err?.['traceId'] as string) ?? response.headers?.['x-trace-id'],
        fieldErrors: err?.['fieldErrors'],
      });
    }
    return response;
  },
  (error) => {
    const errData = error.response?.data?.error as Record<string, unknown> | undefined;
    const code = errData?.['code'] ?? `HTTP-${error.response?.status ?? 0}`;
    const message =
      (errData?.['message'] as string) ??
      (error.response?.data?.message as string) ??
      error.message ??
      'Network error';
    const traceId = (error.response?.headers?.['x-trace-id'] as string) ?? undefined;
    const fieldErrors = errData?.['fieldErrors'];
    return Promise.reject({ code, message, traceId, fieldErrors });
  }
);

export const myProfileApi = {
  /** GET /api/eraf/me — 내 프로필 조회 */
  get: (): Promise<MyProfileResponse> =>
    apiClient
      .get<unknown, { data: MyProfileResponse }>('/me')
      .then((res) => res.data),

  /** PATCH /api/eraf/me — 기본정보 수정 */
  update: (body: UpdateProfileRequest): Promise<MyProfileResponse> =>
    apiClient
      .patch<unknown, { data: MyProfileResponse }>('/me', body)
      .then((res) => res.data),

  /** POST /api/eraf/me/password-change — 비밀번호 변경 */
  changePassword: (body: ChangePasswordRequest): Promise<ChangePasswordResponse> =>
    apiClient
      .post<unknown, { data: ChangePasswordResponse }>('/me/password-change', body)
      .then((res) => res.data),

  /** POST /api/eraf/me/avatar — 아바타 업로드 (multipart) */
  uploadAvatar: (file: File): Promise<AvatarUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient
      .post<unknown, { data: AvatarUploadResponse }>('/me/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((res) => res.data);
  },

  /** DELETE /api/eraf/me/avatar — 아바타 삭제 */
  deleteAvatar: (): Promise<void> =>
    apiClient.delete('/me/avatar').then(() => undefined),
};
